/**
 * Upload dashboard component — shows all deliverable upload history.
 */

import { useEffect, useRef, useState } from 'react';
import { Button, Collapse, Popconfirm, Radio, Select, Table, Tag, Tooltip, Popover, Typography } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import type { UploadRecord, UploadedFileItem, UploadRecordFilterOptions } from '@/api/uploadRecords';
import { listUploadRecords, getUploadRecordFilterOptions, deleteUploadRecord } from '@/api/uploadRecords';
import { formatFileSize, localeFor } from '@/utils';
import { getDeliverableTypeLabels, getSourceFilters, getSourceLabels } from '@/constants/upload';
import { t as tGlobal, useT } from '@/i18n';

const { Text } = Typography;

// 展示态取值与项目空间那一列同源（交付物级，不含文件级的 queued）；
// 文案也照 `PackageList` / `ExternalReleaseList` 那一列来，同一状态两处写法得一样
const UPLOAD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  uploading: { label: '上传中', color: 'processing' },
  done: { label: '完成', color: 'success' },
  failed: { label: '失败', color: 'error' },
};

const formatDateTime = (v: string | null | undefined): string => {
  if (!v) return '-';
  return new Date(v).toLocaleString(localeFor(), {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatTime = (v: string | null | undefined): string => {
  if (!v) return '-';
  return new Date(v).toLocaleTimeString(localeFor(), { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const renderStringListPopover = (items: string[], popoverTitle: string, linkText: (n: number) => string) => {
  if (!items || items.length === 0) return '-';
  const content = (
    <div>
      {items.map((item, idx) => (
        <div key={idx} className="py-2 border-b border-gray-100 last:border-0" style={{ padding: '2px 0' }}>
          <Text style={{ fontSize: 12 }}>{item}</Text>
        </div>
      ))}
    </div>
  );
  return (
    <Popover content={content} title={popoverTitle} trigger="hover">
      <Text style={{ cursor: 'pointer', color: '#1677ff' }}>{linkText(items.length)}</Text>
    </Popover>
  );
};

const renderFileLine = (item: UploadedFileItem, idx: number) => (
  <div key={idx} style={{ padding: '4px 0', flexDirection: 'column', alignItems: 'flex-start', display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
    <div>
      {item.file_type_name && (
        <Tag color="blue" style={{ marginRight: 4, fontSize: 11 }}>{item.file_type_name}</Tag>
      )}
      <Text style={{ fontSize: 12 }}>{item.file_name}</Text>
      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>({formatFileSize(item.file_size)})</Text>
    </div>
    {(item.auto_retry_count != null || item.upload_started_at || item.upload_finished_at) && (
      <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
        {tGlobal('自动重试: {count}次', { count: item.auto_retry_count ?? 0 })}
        {' · '}
        {tGlobal('手动重试: {count}次', { count: item.manual_retry_count ?? 0 })}
        {item.upload_started_at || item.upload_finished_at ? (
          <>
            {' · '}
            {tGlobal('上传时间: {start} ~ {end}', { start: formatTime(item.upload_started_at), end: formatTime(item.upload_finished_at) })}
            {item.upload_started_at && item.upload_finished_at ? (
              <>
                {' '}({((new Date(item.upload_finished_at).getTime() - new Date(item.upload_started_at).getTime()) / 1000).toFixed(1)}s
                {item.avg_speed_mbps != null ? `，${item.avg_speed_mbps} MB/s` : ''})
              </>
            ) : null}
          </>
        ) : null}
      </Text>
    )}
  </div>
);

const renderFileListPopover = (files: UploadedFileItem[]) => {
  if (!files || files.length === 0) return '-';

  const hasCategorized = files.some(f => f.change_type);

  if (!hasCategorized) {
    // Legacy: flat list (backward compatible)
    const content = (
      <div>
        {files.map((item, idx) => renderFileLine(item, idx))}
      </div>
    );
    return (
      <Popover content={content} title={tGlobal('上传文件')} trigger="hover" overlayStyle={{ maxWidth: 480 }}>
        <Text style={{ cursor: 'pointer', color: '#1677ff' }}>{tGlobal('{count} 个文件', { count: files.length })}</Text>
      </Popover>
    );
  }

  const added = files.filter(f => f.change_type === 'added');
  const deleted = files.filter(f => f.change_type === 'deleted');
  const failed = files.filter(f => f.change_type === 'failed');
  const existing = files.filter(f => f.change_type === 'existing');

  const collapseItems = existing.length > 0 ? [{
    key: 'existing',
    label: <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{tGlobal('未变更文件 ({count})', { count: existing.length })}</Text>,
    children: <div>{existing.map((item, idx) => renderFileLine(item, idx))}</div>,
  }] : [];

  const content = (
    <div style={{ minWidth: 320 }}>
      {added.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#52c41a', marginBottom: 4 }}>{tGlobal('新增文件 ({count})', { count: added.length })}</div>
          {added.map((item, idx) => renderFileLine(item, idx))}
        </div>
      )}
      {failed.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#fa8c16', marginBottom: 4 }}>{tGlobal('上传失败文件 ({count})', { count: failed.length })}</div>
          {failed.map((item, idx) => renderFileLine(item, idx))}
        </div>
      )}
      {deleted.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#ff4d4f', marginBottom: 4 }}>{tGlobal('删除文件 ({count})', { count: deleted.length })}</div>
          {deleted.map((item, idx) => renderFileLine(item, idx))}
        </div>
      )}
      {collapseItems.length > 0 && (
        <Collapse size="small" ghost items={collapseItems} style={{ marginTop: 4 }} />
      )}
    </div>
  );

  const nonDeleted = files.filter(f => f.change_type !== 'deleted' && f.change_type !== 'failed');
  const label = (deleted.length > 0 || failed.length > 0)
    ? `${tGlobal('{count} 个文件', { count: nonDeleted.length })} (+${added.length}${deleted.length > 0 ? ` -${deleted.length}` : ''}${failed.length > 0 ? ` ✗${failed.length}` : ''})`
    : `${tGlobal('{count} 个文件', { count: nonDeleted.length })}${added.length > 0 ? ` (+${added.length})` : ''}`;

  return (
    <Popover content={content} title={tGlobal('上传文件')} trigger="hover" overlayStyle={{ maxWidth: 520 }}>
      <Text style={{ cursor: 'pointer', color: '#1677ff' }}>{label}</Text>
    </Popover>
  );
};

const toOrdering = (field: string | undefined, order: string | undefined): string | undefined => {
  if (!field || !order) return undefined;
  return order === 'descend' ? `-${field}` : field;
};

const EMPTY_OPTIONS: UploadRecordFilterOptions = {
  project_names: [],
  vehicle_models: [],
  created_by_names: [],
};

const UploadBoard: React.FC = () => {
  const { t } = useT();
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [activeFilters, setActiveFilters] = useState<Record<string, string | undefined>>({
    exclude_project_name: 'TEST',
  });
  const [activeSorter, setActiveSorter] = useState<{ field?: string; order?: string }>({});
  const [filterOptions, setFilterOptions] = useState<UploadRecordFilterOptions>(EMPTY_OPTIONS);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentPageRef = useRef({ page: 1, pageSize: 20 });

  const fetchRecords = async (
    page = 1,
    pageSize = 20,
    filters: Record<string, string | undefined> = {},
    sorter: { field?: string; order?: string } = {},
  ) => {
    setLoading(true);
    try {
      const ordering = toOrdering(sorter.field, sorter.order);
      const data = await listUploadRecords({
        page,
        page_size: pageSize,
        ...filters,
        ...(ordering ? { ordering } : {}),
      });
      setRecords(data.results);
      setPagination((prev) => ({ ...prev, total: data.count, current: page, pageSize }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(1, 20, { exclude_project_name: 'TEST' });
    getUploadRecordFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  // Poll every 5s while any record on the current page is still uploading
  // （含第三方平台上传中：后端派生的展示态给出，前端不感知第三方）
  useEffect(() => {
    const hasUploading = records.some(r => r.display_upload_status === 'uploading');
    if (hasUploading && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchRecords(currentPageRef.current.page, currentPageRef.current.pageSize, activeFilters, activeSorter);
      }, 5000);
    } else if (!hasUploading && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [records]);

  const handleTableChange = (
    p: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UploadRecord> | SorterResult<UploadRecord>[],
  ) => {
    const page = p.current ?? 1;
    const pageSize = p.pageSize ?? 20;
    currentPageRef.current = { page, pageSize };

    const newFilters: Record<string, string | undefined> = {};

    // project_name 列使用 include:/exclude: 前缀编码
    const projectVal = filters['project_name'];
    if (projectVal && projectVal.length > 0) {
      const encoded = String(projectVal[0]);
      if (encoded.startsWith('exclude:')) {
        newFilters['exclude_project_name'] = encoded.slice(8);
      } else {
        newFilters['project_name'] = encoded.startsWith('include:') ? encoded.slice(8) : encoded;
      }
    }

    const filterKeys: Array<[string, string]> = [
      ['deliverable_type', 'deliverable_type'],
      ['source', 'source'],
      ['vehicle_model', 'vehicle_model'],
      ['upload_status', 'upload_status'],
      ['created_by_name', 'created_by_name'],
    ];
    for (const [colKey, paramKey] of filterKeys) {
      const val = filters[colKey];
      newFilters[paramKey] = val && val.length > 0 ? String(val[0]) : undefined;
    }

    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const newSorter = {
      field: s.field as string | undefined,
      order: s.order as string | undefined,
    };

    setActiveFilters(newFilters);
    setActiveSorter(newSorter);
    fetchRecords(page, pageSize, newFilters, newSorter);
  };

  const handleDelete = async (id: number) => {
    await deleteUploadRecord(id);
    fetchRecords(currentPageRef.current.page, currentPageRef.current.pageSize, activeFilters, activeSorter);
  };

  const sortOrder = (field: string) =>
    activeSorter.field === field ? (activeSorter.order as 'ascend' | 'descend' | null) : null;

  const toFilterItems = (values: string[]) => values.map(v => ({ text: v, value: v }));

  const columns: ColumnsType<UploadRecord> = [
    {
      title: t('项目名'),
      dataIndex: 'project_name',
      key: 'project_name',
      width: 120,
      fixed: 'left',
      filteredValue: activeFilters['exclude_project_name']
        ? [`exclude:${activeFilters['exclude_project_name']}`]
        : activeFilters['project_name']
        ? [`include:${activeFilters['project_name']}`]
        : null,
      filterIcon: (filtered: boolean) => (
        <FilterFilled style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const current = selectedKeys[0] ? String(selectedKeys[0]) : '';
        const initMode = current.startsWith('exclude:') ? 'exclude' : 'include';
        const initName = current.startsWith('include:') || current.startsWith('exclude:')
          ? current.slice(8)
          : current;
        const [mode, setMode] = useState<'include' | 'exclude'>(initMode);
        const [name, setName] = useState<string>(initName);
        return (
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Radio.Group value={mode} onChange={e => setMode(e.target.value)}>
              <Radio value="include">{t('只显示')}</Radio>
              <Radio value="exclude">{t('不显示')}</Radio>
            </Radio.Group>
            <Select
              showSearch
              placeholder={t('选择项目名')}
              value={name || undefined}
              onChange={(v: string) => setName(v)}
              style={{ width: 180 }}
              options={filterOptions.project_names.map(n => ({ label: n, value: n }))}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                size="small"
                disabled={!name}
                onClick={() => { setSelectedKeys([`${mode}:${name}`]); confirm(); }}
              >{t('确定')}</Button>
              <Button
                size="small"
                onClick={() => { clearFilters?.(); setMode('include'); setName(''); confirm(); }}
              >{t('重置')}</Button>
            </div>
          </div>
        );
      },
    },
    {
      title: t('交付物ID'),
      dataIndex: 'deliverable_id',
      key: 'deliverable_id',
      width: 90,
      sorter: true,
      sortOrder: sortOrder('deliverable_id'),
    },
    {
      title: t('交付类型'),
      dataIndex: 'deliverable_type',
      key: 'deliverable_type',
      width: 150,
      filters: [
        { text: t('底软交付物'), value: 'package' },
        { text: t('Momenta释放产物包'), value: 'external_release' },
      ],
      filterMultiple: false,
      render: (v: string) => {
        const meta = getDeliverableTypeLabels(t)[v];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : v;
      },
    },
    {
      title: t('来源'),
      dataIndex: 'source',
      key: 'source',
      width: 100,
      filters: getSourceFilters(t),
      filterMultiple: false,
      render: (v: string) => {
        const meta = getSourceLabels(t)[v];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : v;
      },
    },
    {
      title: t('上传类型'),
      dataIndex: 'is_first_upload',
      key: 'is_first_upload',
      width: 90,
      render: (_: boolean, record: UploadRecord) => {
        if (record.upload_type === 'retry') return <Tag color="orange">{t('重传')}</Tag>;
        return <Tag color={record.is_first_upload ? 'green' : 'blue'}>{record.is_first_upload ? t('新建') : t('更新')}</Tag>;
      },
    },
    {
      title: t('交付物名称'),
      dataIndex: 'deliverable_name',
      key: 'deliverable_name',
      width: 200,
      ellipsis: true,
      render: (v: string) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: t('车型'),
      dataIndex: 'vehicle_model',
      key: 'vehicle_model',
      width: 100,
      filters: toFilterItems(filterOptions.vehicle_models),
      filterMultiple: false,
    },
    {
      title: t('文件数量'),
      dataIndex: 'file_count',
      key: 'file_count',
      width: 90,
      align: 'right',
    },
    {
      title: t('文件大小'),
      dataIndex: 'total_size',
      key: 'total_size',
      width: 110,
      align: 'right',
      render: (v: number) => formatFileSize(v),
    },
    {
      // 值取后端派生的展示态（与项目空间那一列一致，含第三方上传中），筛选参数名仍是 upload_status
      title: t('上传状态'),
      dataIndex: 'display_upload_status',
      key: 'upload_status',
      width: 100,
      filters: [
        { text: t('上传中'), value: 'uploading' },
        { text: t('完成'), value: 'done' },
        { text: t('失败'), value: 'failed' },
      ],
      filterMultiple: false,
      render: (v: string) => {
        const meta = UPLOAD_STATUS_LABELS[v];
        return meta ? <Tag color={meta.color}>{t(meta.label)}</Tag> : v;
      },
    },
    {
      title: t('上传者'),
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 100,
      filters: toFilterItems(filterOptions.created_by_names),
      filterMultiple: false,
    },
    {
      title: t('开始上传时间'),
      dataIndex: 'started_at',
      key: 'started_at',
      width: 170,
      sorter: true,
      sortOrder: sortOrder('started_at'),
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('结束上传时间'),
      dataIndex: 'finished_at',
      key: 'finished_at',
      width: 170,
      sorter: true,
      sortOrder: sortOrder('finished_at'),
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: t('上传时长'),
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      width: 100,
      align: 'right',
      sorter: true,
      sortOrder: sortOrder('duration_seconds'),
      render: (v: number | null) => (v != null ? `${v.toFixed(1)}s` : '-'),
    },
    {
      title: t('平均网速'),
      dataIndex: 'speed_mbps',
      key: 'speed_mbps',
      width: 110,
      align: 'right',
      sorter: true,
      sortOrder: sortOrder('speed_mbps'),
      render: (v: number | null) => (v != null ? `${v} MB/s` : '-'),
    },
    {
      title: t('上传文件'),
      dataIndex: 'uploaded_files',
      key: 'uploaded_files',
      width: 120,
      render: (files: UploadedFileItem[]) => renderFileListPopover(files),
    },
    {
      title: t('关联文件'),
      dataIndex: 'linked_items',
      key: 'linked_items',
      width: 120,
      render: (items: string[]) =>
        renderStringListPopover(items, t('关联文件'), (n) => t('{count} 个', { count: n })),
    },
    {
      title: t('说明'),
      dataIndex: 'description',
      key: 'description',
      width: 150,
      ellipsis: true,
      render: (v: string) =>
        v ? <Tooltip title={v}>{v}</Tooltip> : '-',
    },
    {
      title: t('操作'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: UploadRecord) => (
        <Popconfirm
          title={t('确认删除此上传记录？')}
          onConfirm={() => handleDelete(record.id)}
          okText={t('删除')}
          cancelText={t('取消')}
          okButtonProps={{ danger: true }}
        >
          <Button type="link" danger size="small">{t('删除')}</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Table<UploadRecord>
      columns={columns}
      dataSource={records}
      rowKey="id"
      loading={loading}
      scroll={{ x: 1600 }}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showTotal: (total) => t('共 {total} 条', { total }),
        pageSizeOptions: ['20', '50', '100'],
      }}
      onChange={handleTableChange}
      size="small"
    />
  );
};

export default UploadBoard;
